import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface SubscriptionInfo {
  hasActiveSubscription: boolean;
  currentPlan?: 'mensal' | 'anual';
  canBuyMonthly: boolean;
  canBuyAnnual: boolean;
  canBuyCredits: boolean;
  subscriptionExpiresAt?: string;
  message: string;
}

interface CreditsData {
  availableCredits: number;
  isLoading: boolean;
  error: string | null;
  subscriptionInfo: SubscriptionInfo | null;
  refetch: () => Promise<void>;
}

export function useCredits(): CreditsData {
  const { user } = useAuth();
  const [availableCredits, setAvailableCredits] = useState(0);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);

  const fetchCredits = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // First get the profile ID
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (profileError) {
        throw new Error("Erro ao buscar perfil");
      }

      setProfileId(profile.id);

      // Get the credits
      const { data: credits, error: creditsError } = await supabase
        .from("credits")
        .select("available_credits")
        .eq("user_id", profile.id)
        .single();

      if (creditsError) {
        if (creditsError.code === "PGRST116") {
          setAvailableCredits(0);
        } else {
          throw new Error("Erro ao buscar créditos");
        }
      } else {
        setAvailableCredits(credits?.available_credits ?? 0);
      }

      // Get subscription status - using type assertion since RPC returns Json
      const { data: subStatus, error: subError } = await supabase
        .rpc('check_subscription_status' as any, {
          p_user_id: profile.id
        }) as {
          data: {
            has_active_subscription: boolean;
            current_plan?: 'mensal' | 'anual';
            can_buy_monthly: boolean;
            can_buy_annual: boolean;
            can_buy_credits: boolean;
            subscription_expires_at?: string;
            message: string;
          } | null; error: any
        };

      if (!subError && subStatus) {
        setSubscriptionInfo({
          hasActiveSubscription: subStatus.has_active_subscription,
          currentPlan: subStatus.current_plan,
          canBuyMonthly: subStatus.can_buy_monthly,
          canBuyAnnual: subStatus.can_buy_annual,
          canBuyCredits: subStatus.can_buy_credits,
          subscriptionExpiresAt: subStatus.subscription_expires_at,
          message: subStatus.message,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Fetch inicial
  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Realtime subscription para mudanças em credits
  useEffect(() => {
    if (!profileId) return;

    console.log('[useCredits] Setting up realtime subscription for profileId:', profileId);

    const channel = supabase
      .channel(`credits-realtime-${profileId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'credits',
          filter: `user_id=eq.${profileId}`
        },
        (payload) => {
          console.log('[useCredits] Realtime credits update:', payload);

          if (payload.eventType === 'UPDATE' && payload.new?.available_credits !== undefined) {
            setAvailableCredits(payload.new.available_credits);
          } else {
            fetchCredits();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'credit_transactions',
          filter: `user_id=eq.${profileId}`
        },
        () => {
          console.log('[useCredits] New transaction, refreshing credits...');
          fetchCredits();
        }
      )
      .subscribe((status) => {
        console.log('[useCredits] Realtime subscription status:', status);
      });

    return () => {
      console.log('[useCredits] Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [profileId, fetchCredits]);

  return {
    availableCredits,
    isLoading,
    error,
    subscriptionInfo,
    refetch: fetchCredits,
  };
}

